import { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';

export function Lightbox({ images, initialIndex = 0, onClose }) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  const goNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  }, [images.length]);

  const goPrev = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  }, [images.length]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
    };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [onClose, goNext, goPrev]);

  // Preload adjacent images so next/prev feel instant even when the proxy
  // strips HTTP caching (e.g. in preview environments).
  useEffect(() => {
    if (!images?.length) return;
    const total = images.length;
    const idxs = [
      (currentIndex + 1) % total,
      (currentIndex - 1 + total) % total,
      (currentIndex + 2) % total,
    ];
    idxs.forEach((i) => {
      const img = new window.Image();
      img.decoding = 'async';
      img.src = images[i];
    });
  }, [currentIndex, images]);

  return (
    <div
      className="fixed inset-0 z-[200] bg-black/95 flex items-center justify-center"
      onClick={onClose}
      data-testid="lightbox-overlay"
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
        data-testid="lightbox-close"
      >
        <X size={28} />
      </button>

      {/* Counter */}
      <div className="absolute top-4 left-4 text-white/70 font-sans text-sm z-10">
        {currentIndex + 1} / {images.length}
      </div>

      {/* Image stack — only render current ± 1 to avoid opening 50 images
          at once when the annuncio has a large gallery. */}
      <div
        className="relative w-full h-full flex items-center justify-center px-16"
        onClick={(e) => e.stopPropagation()}
      >
        {images.map((img, idx) => {
          const total = images.length;
          const isNearby =
            idx === currentIndex ||
            idx === (currentIndex + 1) % total ||
            idx === (currentIndex - 1 + total) % total;
          if (!isNearby) return null;
          return (
            <img
              key={idx}
              src={img}
              alt={idx === currentIndex ? `Foto ${idx + 1}` : ''}
              className={`absolute max-w-full max-h-[90vh] object-contain select-none transition-opacity duration-200 ${idx === currentIndex ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
              draggable={false}
              decoding="async"
              fetchPriority={idx === currentIndex ? 'high' : 'low'}
              loading={idx === currentIndex ? 'eager' : 'lazy'}
              data-testid={idx === currentIndex ? 'lightbox-image' : undefined}
            />
          );
        })}
      </div>

      {/* Navigation */}
      {images.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); goPrev(); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
            data-testid="lightbox-prev"
          >
            <ChevronLeft size={32} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); goNext(); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
            data-testid="lightbox-next"
          >
            <ChevronRight size={32} />
          </button>
        </>
      )}

      {/* Thumbnails bar */}
      {images.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 max-w-[90vw] overflow-x-auto p-2">
          {images.map((img, index) => (
            <button
              key={index}
              onClick={(e) => { e.stopPropagation(); setCurrentIndex(index); }}
              className={`flex-shrink-0 w-16 h-12 rounded-md overflow-hidden border-2 transition-all ${
                index === currentIndex ? 'border-white opacity-100' : 'border-transparent opacity-50 hover:opacity-80'
              }`}
            >
              <img src={img} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
